import { useState, useEffect, useRef } from 'react';
import { C } from './shared';

const PRESET_MIN   = { prof_months: 70, worst_month: -5,  calmar: 0.2, sharpe: 1.0 };
const PRESET_IDEAL = { prof_months: 80, worst_month: -3,  calmar: 0.5, sharpe: 1.5 };

const TABLE_ROWS = [
  { label: 'Profit.Mo %', min: '≥ 70%', ideal: '≥ 80%' },
  { label: 'Worst Mo %',  min: '≥ −5%', ideal: '≥ −3%' },
  { label: 'Calmar',      min: '≥ 0.2', ideal: '≥ 0.5' },
  { label: 'Sharpe',      min: '≥ 1.0', ideal: '≥ 1.5' },
];

export default function StablePresetButton({ onApply }) {
  const [open, setOpen]             = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom]         = useState({ ...PRESET_IDEAL });
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const apply = (thresholds, presetName) => {
    onApply(thresholds, presetName);
    setOpen(false);
  };

  return (
    <div ref={popRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Main button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '6px 12px', borderRadius: 7, cursor: 'pointer',
          fontSize: 13, fontWeight: 500,
          border: `1px solid ${open ? C.primary : C.border}`,
          background: open ? `${C.primary}12` : '#fff',
          color: open ? C.primary : C.textMd,
          whiteSpace: 'nowrap',
        }}
      >
        <span>🛡️ Стабільні</span>
        <span style={{ fontSize: 10, color: open ? C.primary : C.textSm }}>▾</span>
      </button>

      {/* Popover */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 2000,
          background: '#fff', border: `1px solid ${C.border}`,
          borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          padding: '14px 16px', minWidth: 280,
        }}>
          {/* Header table */}
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMd, marginBottom: 8 }}>
            Критерії відбору стабільних стратегій
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 12, fontSize: 12 }}>
            <thead>
              <tr>
                <th style={thStyle}>Показник</th>
                <th style={thStyle}>Мінімум</th>
                <th style={{ ...thStyle, color: C.primary }}>Ідеал</th>
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map(r => (
                <tr key={r.label}>
                  <td style={tdStyle}>{r.label}</td>
                  <td style={tdStyle}>{r.min}</td>
                  <td style={{ ...tdStyle, color: C.primary, fontWeight: 600 }}>{r.ideal}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button
              onClick={() => apply(PRESET_MIN, 'min')}
              style={btnOutlineStyle}
            >
              Застосувати «Мінімум»
            </button>
            <button
              onClick={() => apply(PRESET_IDEAL, 'ideal')}
              style={btnPrimaryStyle}
            >
              Застосувати «Ідеал»
            </button>
          </div>

          {/* Custom thresholds collapsible */}
          <div>
            <button
              onClick={() => setCustomOpen(o => !o)}
              style={{
                background: 'none', border: 'none', padding: 0,
                fontSize: 12, color: C.textMd, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <span style={{ fontSize: 10 }}>{customOpen ? '▲' : '▾'}</span>
              <span>Власні пороги</span>
            </button>

            {customOpen && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', marginBottom: 10 }}>
                  {[
                    { key: 'prof_months', label: 'Profit.Mo %', step: '1' },
                    { key: 'worst_month', label: 'Worst Mo %',  step: '0.1' },
                    { key: 'calmar',      label: 'Calmar',      step: '0.05' },
                    { key: 'sharpe',      label: 'Sharpe',      step: '0.1' },
                  ].map(({ key, label, step }) => (
                    <div key={key}>
                      <div style={{ fontSize: 11, color: C.textMd, marginBottom: 3 }}>{label} ≥</div>
                      <input
                        type="number"
                        step={step}
                        value={custom[key]}
                        onChange={e => setCustom(prev => ({ ...prev, [key]: Number(e.target.value) }))}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => apply(custom, 'custom')}
                  style={btnPrimaryStyle}
                >
                  Застосувати
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '4px 8px', textAlign: 'left', fontSize: 11,
  color: '#64748b', fontWeight: 600, borderBottom: '1px solid #E2E8F0',
};
const tdStyle = {
  padding: '4px 8px', fontSize: 12, color: '#0F172A',
  borderBottom: '1px solid #F1F5F9',
};
const btnOutlineStyle = {
  flex: 1, padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
  fontSize: 12, fontWeight: 500,
  border: `1px solid ${C.primary}`, background: 'transparent', color: C.primary,
};
const btnPrimaryStyle = {
  flex: 1, padding: '6px 10px', borderRadius: 7, cursor: 'pointer',
  fontSize: 12, fontWeight: 600,
  border: 'none', background: C.primary, color: '#fff',
};
const inputStyle = {
  width: '100%', padding: '5px 7px', border: '1px solid #E2E8F0',
  borderRadius: 7, fontSize: 12, color: '#0F172A', background: '#fff',
  boxSizing: 'border-box', outline: 'none',
};
